import {
  Button,
  Dialog,
  Icon,
  IconNames,
  Intent,
  Side,
  Tag,
  Tooltip,
  type IColumnDefsConfig,
  type IconName,
} from "@focus/focusUI";
import { useState } from "react";
import type { ColumnFiltersState } from "../../services/helpers/pager";
import type { OnChangeFn } from "./useFocusocTable";

const mapOperatorToLabel: Record<string, string> = {
  equals: "Equals",
  notEqual: "Not equals",
  greaterThan: "Greater than",
  lessThan: "Less than",
  greaterThanOrEqual: "Greater than or equal",
  lessThanOrEqual: "Less than or equal",
  contains: "Contains",
  notContains: "Does not contain",
  startsWith: "Starts with",
  endsWith: "Ends with",
  blank: "Is blank",
  notBlank: "Is not blank",
  inRange: "Is between",
};

interface FiltersTagProps {
  columnFilters: ColumnFiltersState;
  columnFiltersChange: OnChangeFn<ColumnFiltersState>;
  columnDefinitions: IColumnDefsConfig[];
}

export const FiltersTag = ({
  columnFilters,
  columnFiltersChange,
  columnDefinitions,
}: FiltersTagProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  // Helper to get column info
  const getColInfo = (id: string) => {
    const col = columnDefinitions.find((c) => c.key === id);
    let title = id;
    if (col) {
      if (typeof col.headerName === "string") {
        title = col.headerName;
      } else if (typeof col.headerName === "function") {
        title = col.key;
      }
    }
    return col ? { title, type: col.cellType } : { title: id, type: "unknown" };
  };

  const hasFilters = columnFilters && columnFilters.length > 0;

  return (
    hasFilters && (
      <>
        <Tooltip
          limitedSize
          usePortal={false}
          content={
            hasFilters ? (
              <div style={{ minWidth: 200 }}>
                {columnFilters.map((filter, idx) => {
                  const { title } = getColInfo(filter.id);
                  return (
                    <div key={filter.id + idx} style={{ marginBottom: 6 }}>
                      <strong>{title}</strong> <br />
                      <span style={{ fontSize: 13 }}>
                        <em>{mapOperatorToLabel[filter.operator]}</em>
                        {filter.operator !== "blank" &&
                          filter.operator !== "notBlank" &&
                          `: ${
                            Array.isArray(filter.value)
                              ? filter.value.join(", ")
                              : String(filter.value)
                          }`}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <span>No filters applied</span>
            )
          }
          side={Side.BOTTOM}
        >
          <Tag
            style={{ margin: "0 3px" }}
            round
            intent={Intent.NONE}
            rightIcon={
              hasFilters ? (
                <Icon
                  role="filters-removed"
                  color="white"
                  style={{ cursor: "pointer" }}
                  icon={IconNames.FILTER_REMOVE as IconName}
                  size={14}
                  onClick={() => setDialogOpen(true)}
                />
              ) : undefined
            }
          >
            {"Filters applied"}
          </Tag>
        </Tooltip>
        <Dialog
          icon={IconNames.FILTER_REMOVE}
          isOpen={dialogOpen}
          onClose={() => setDialogOpen(false)}
          title="Remove all filters"
          canEscapeKeyClose
          canOutsideClickClose
        >
          <div style={{ padding: 16 }}>
            <p>Are you sure you want to remove all filters?</p>
            <p>This action cannot be undone.</p>
            <div
              style={{
                marginTop: 24,
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
              }}
            >
              <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                intent={Intent.PRIMARY}
                onClick={() => {
                  columnFiltersChange([]);
                  setDialogOpen(false);
                }}
              >
                Accept
              </Button>
            </div>
          </div>
        </Dialog>
      </>
    )
  );
};
